/* assets/impact-feature-chart.js
   适配 Kalles + Impact 对比表格，解决 View All/View Less 错乱问题
*/
(() => {
  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  const animateHeight = (el, fromPx, toPx, { duration = 250, easing = "ease" } = {}) => {
    if (!el || typeof el.animate !== "function" || prefersReducedMotion()) {
      el.style.height = `${toPx}px`;
      return Promise.resolve();
    }

    const anim = el.animate(
      [{ height: `${fromPx}px` }, { height: `${toPx}px` }],
      { duration, easing, fill: "forwards" }
    );
    return anim.finished.catch(() => {});
  };

  const rafThrottle = (fn) => {
    let raf = 0;
    return (...args) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        fn(...args);
      });
    };
  };

  class FeatureChart extends HTMLElement {
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      // 1. 获取核心元素
      this.viewButtonElement = this.querySelector('[data-action="toggle-rows"]');
      this.featureChartTable = this.querySelector(".feature-chart__table");
      this.featureProductRow = this.querySelector(".feature-chart__table-row--product");
      this.featureChartSticky = this.querySelector(".feature-chart__table-row--sticky");
      this.maxRows = Number.parseInt(this.getAttribute("max-rows") || "0", 10);

      if (!this.featureChartTable || !this.viewButtonElement) return;

      // 2. 仅选择普通对比行（排除产品行/粘性行）
      this.featureChartRows = Array.from(
        this.featureChartTable.querySelectorAll(
          '.feature-chart__table-row:not(.feature-chart__table-row--product):not(.feature-chart__table-row--sticky)'
        )
      );

      // 3. 初始化：隐藏超出 max_rows 的行（和原版 Liquid 效果一致）
      if (this.maxRows > 0 && this.featureChartRows.length > this.maxRows) {
        this.featureChartRows.slice(this.maxRows).forEach(row => {
          row.hidden = true;
        });
      }

      // 4. 绑定按钮事件
      this.viewButtonElement.addEventListener("click", () => this._toggleRows());
      if (this.featureChartSticky && this.featureProductRow) {
        this._setupSticky();
      }
    }

    disconnectedCallback() {
      if (this.viewButtonElement) {
        this.viewButtonElement.removeEventListener("click", () => this._toggleRows());
      }
      if (this.featureChartTable && this._onHScroll) {
        this.featureChartTable.removeEventListener("scroll", this._onHScroll);
      }
      if (this._onPageScroll) {
        window.removeEventListener("scroll", this._onPageScroll);
        window.removeEventListener("resize", this._onPageScroll);
      }
      if (this._ro) this._ro.disconnect();
    }

    _setupSticky() {
      const sticky = this.featureChartSticky;
      const table = this.featureChartTable;

      const setStickyWidth = () => {
        sticky.style.width = `${table.scrollWidth}px`;
      };
      setStickyWidth();

      this._onHScroll = (event) => {
        sticky.style.marginLeft = -1 * event.target.scrollLeft + "px";
      };
      table.addEventListener("scroll", this._onHScroll);

      this._ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width;
        if (w) sticky.style.width = `${w}px`;
        setStickyWidth();
      });
      this._ro.observe(table);

      const offsetStr = getComputedStyle(this).scrollPaddingTop || "0px";
      const offsetPx = Number.parseFloat(offsetStr) || 0;

      const updateVisibility = rafThrottle(() => {
        const rect = table.getBoundingClientRect();
        const vpH = window.innerHeight || 0;
        const travel = Math.max(1, rect.height - vpH + offsetPx * 2);
        const progressed = (offsetPx - rect.top) / travel;
        const progress = clamp(progressed, 0, 1);
        const current = -rect.top + offsetPx;

        const show =
          current >= this.featureProductRow.clientHeight / 2 &&
          progress < 0.85 &&
          rect.bottom > offsetPx;

        sticky.classList.toggle("is-visible", show);
      });

      this._onPageScroll = updateVisibility;
      window.addEventListener("scroll", updateVisibility);
      window.addEventListener("resize", updateVisibility);
      updateVisibility();
    }

    _toggleRows() {
      this.classList.contains("is-expanded") ? this._hideRows() : this._showRows();
    }

    async _showRows() {
      const table = this.featureChartTable;
      const fromHeight = table.clientHeight;

      // 锁定高度，避免跳动
      table.style.height = `${fromHeight}px`;
      // 显示所有行
      this.featureChartRows.forEach(row => {
        row.hidden = false;
      });

      // 计算展开后的高度
      const toHeight = table.scrollHeight;
      // 更新按钮文字
      const toggleText = this.viewButtonElement.querySelector(".feature-chart__toggle-text");
      if (toggleText) {
        toggleText.innerText = this.viewButtonElement.getAttribute("data-view-less") || "";
      }

      this.classList.add("is-expanded");
      await animateHeight(table, fromHeight, toHeight);
      table.style.height = "auto";
    }

    async _hideRows() {
      const table = this.featureChartTable;
      const fromHeight = table.clientHeight;

      // 计算收起后的高度（产品行 + 前N行对比行）
      let toHeight = this.featureProductRow ? this.featureProductRow.clientHeight : 0;
      this.featureChartRows.slice(0, this.maxRows).forEach(row => {
        toHeight += row.clientHeight || 0;
      });

      // 锁定高度，执行动画
      table.style.height = `${fromHeight}px`;
      // 更新按钮文字
      const toggleText = this.viewButtonElement.querySelector(".feature-chart__toggle-text");
      if (toggleText) {
        toggleText.innerText = this.viewButtonElement.getAttribute("data-view-more") || "";
      }

      this.classList.remove("is-expanded");
      await animateHeight(table, fromHeight, toHeight);
      // 隐藏超出的行
      this.featureChartRows.slice(this.maxRows).forEach(row => {
        row.hidden = true;
      });
      table.style.height = "auto";
    }
  }

  // 定义自定义元素
  if (!window.customElements.get("feature-chart")) {
    window.customElements.define("feature-chart", FeatureChart);
  }
})();